import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { toggledInSet } from '../core/iching-set.util';
import { IChingFootnote, IChingHexagramCommentary, IChingSegment } from '../core/iching.models';
import { HdIChingProseComponent } from './hd-iching-prose.component';

type CommentarySectionId =
  | 'rulers'
  | 'sequence'
  | 'miscellaneous'
  | 'appendedJudgements'
  | 'judgementCommentary'
  | 'image'
  | 'lines'
  | 'remarks'
  | 'wenYan';

type SegmentSectionId = 'sequence' | 'miscellaneous' | 'appendedJudgements' | 'judgementCommentary' | 'image';

const SECTION_ORDER: readonly CommentarySectionId[] = [
  'rulers',
  'sequence',
  'miscellaneous',
  'appendedJudgements',
  'judgementCommentary',
  'image',
  'lines',
  'remarks',
  'wenYan',
];

/**
 * Boek III (de commentaren) van één hexagram, als inklapbare secties. Secties
 * zonder inhoud verschijnen niet. Voetnoten hangen aan paden onder `commentary.`.
 */
@Component({
  selector: 'hd-iching-commentary',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdIChingProseComponent],
  templateUrl: './hd-iching-commentary.component.html',
  styles: `
    :host { display: block; }
    .iching-commentary__title { margin: 0 0 8px; font-size: 16px; font-weight: 700; }
    .iching-commentary__section + .iching-commentary__section { border-top: 1px solid var(--divider); }
    .iching-commentary__toggle {
      display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 0;
      border: 0; background: none; color: inherit; font: inherit; font-weight: 600; text-align: left; cursor: pointer;
    }
    .iching-commentary__chevron { display: inline-block; width: 1em; color: var(--primary); transition: transform 0.15s ease; }
    .iching-commentary__toggle[aria-expanded='true'] .iching-commentary__chevron { transform: rotate(90deg); }
    .iching-commentary__body { display: flex; flex-direction: column; gap: 8px; padding: 0 0 12px 1.5em; }
    .iching-commentary__line-label, .iching-commentary__ref { font-weight: 600; }
    .iching-commentary__line { display: flex; flex-direction: column; gap: 4px; }
    h5 { margin: 4px 0 0; font-size: 14px; font-weight: 700; }
    .iching-verse { padding-left: 1em; font-size: 1.05em; }
    .iching-prose { color: var(--text-secondary, inherit); }
  `,
})
export class HdIChingCommentaryComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly commentary = input.required<IChingHexagramCommentary>();
  readonly footnotes = input<readonly IChingFootnote[]>([]);

  protected readonly openSections = signal(new Set<CommentarySectionId>());

  /** Secties met inhoud, in boekvolgorde. */
  protected readonly sections = computed<readonly CommentarySectionId[]>(() => {
    const c = this.commentary();
    return SECTION_ORDER.filter(id => this.hasContent(c, id));
  });

  protected t(key: string, params?: Record<string, unknown>): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, params, this.contentLang.effectiveLang());
  }

  protected segments(id: CommentarySectionId): readonly IChingSegment[] {
    if (!isSegmentSection(id)) {
      return [];
    }
    return this.commentary()[id] ?? [];
  }

  protected toggle(id: CommentarySectionId): void {
    this.openSections.update(set => toggledInSet(set, id));
  }

  private hasContent(c: IChingHexagramCommentary, id: CommentarySectionId): boolean {
    switch (id) {
      case 'rulers':
        return c.rulers.length > 0;
      case 'lines':
        return c.lines.length > 0 || !!c.linesIntro?.length || !!c.allLines;
      case 'remarks':
        return !!c.remarks?.length;
      case 'wenYan':
        return !!c.wenYan && (c.wenYan.intro.length > 0 || c.wenYan.items.length > 0);
      default:
        return (c[id]?.length ?? 0) > 0;
    }
  }
}

const SEGMENT_SECTIONS: readonly CommentarySectionId[] = ['sequence', 'miscellaneous', 'appendedJudgements', 'judgementCommentary', 'image'];

function isSegmentSection(id: CommentarySectionId): id is SegmentSectionId {
  return SEGMENT_SECTIONS.includes(id);
}
